import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Switch, FormControlLabel, Grid, Box, MenuItem,
  makeStyles, CircularProgress, Typography, Select, InputLabel,
  FormControl, IconButton, Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import CodeIcon from '@material-ui/icons/Code';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FavoriteIcon from '@material-ui/icons/Favorite';

import MultiLangTextField from '../../components/MultiLangTextField';
import { getDuaById, createDua, updateDua } from '../../services/duaService';
import { getAllDuaSubcategories } from '../../services/duaSubcategoryService';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'clean'],
    [{ color: [] }, { background: [] }],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link',
  'color', 'background',
];

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionCard: {
    padding: 20,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  arabicField: {
    '& .MuiInputBase-root': {
      direction: 'rtl',
      fontFamily: '"Traditional Arabic", "Scheherazade New", "Amiri", serif',
      fontSize: '1.45rem',
      lineHeight: 2.0,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    },
  },
  selectControl: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#10b981',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  editorContainer: {
    marginBottom: theme.spacing(2.5),
    '& .ql-toolbar': {
      backgroundColor: theme.palette.type === 'dark' ? '#1e293b' : '#f1f5f9',
      borderColor: theme.palette.divider,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      '& .ql-stroke': { stroke: theme.palette.text.secondary },
      '& .ql-fill': { fill: theme.palette.text.secondary },
      '& .ql-picker': { color: theme.palette.text.secondary },
    },
    '& .ql-container': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.4)' : '#ffffff',
      borderColor: theme.palette.divider,
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      minHeight: 160,
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.95rem',
      color: theme.palette.text.primary,
    },
  },
  editorLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

export default function DuaFormModal({ open, onClose, duaId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(duaId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [showHTML, setShowHTML] = useState({ translationEn: false, translationBn: false });
  const [showPreview, setShowPreview] = useState({ translationEn: false, translationBn: false });
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [groupedSubcategories, setGroupedSubcategories] = useState({});

  const [form, setForm] = useState({
    title: { en: '', bn: '' },
    textArabic: '',
    transliteration: { en: '', bn: '' },
    translation: { en: '', bn: '' },
    category: 'daily',
    subCategoryId: '',
    audioUrl: '',
    isPublished: true,
  });

  useEffect(() => {
    let mounted = true;
    getAllDuaSubcategories().then((subs) => {
      if (!mounted) return;
      const filtered = subs.filter((s) => !s.isDeleted);
      setAllSubcategories(filtered);
      const grouped = filtered.reduce((acc, sub) => {
        if (!acc[sub.categoryId]) acc[sub.categoryId] = [];
        acc[sub.categoryId].push(sub);
        return acc;
      }, {});
      setGroupedSubcategories(grouped);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (duaId) {
      setLoading(true);
      getDuaById(duaId)
        .then((data) => {
          if (data) {
            setForm({
              title: { en: data.title?.en || '', bn: data.title?.bn || '' },
              textArabic: data.textArabic || '',
              transliteration: { en: data.transliteration?.en || '', bn: data.transliteration?.bn || '' },
              translation: { en: data.translation?.en || '', bn: data.translation?.bn || '' },
              category: data.category || '',
              subCategoryId: data.subCategoryId || '',
              audioUrl: data.audioUrl || '',
              isPublished: data.isPublished !== undefined ? data.isPublished : true,
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching dua:', err);
          setErrorMsg('Failed to load Dua data.');
          setLoading(false);
        });
    } else {
      setForm({
        title: { en: '', bn: '' },
        textArabic: '',
        transliteration: { en: '', bn: '' },
        translation: { en: '', bn: '' },
        category: 'daily',
        subCategoryId: '',
        audioUrl: '',
        isPublished: true,
      });
      setLoading(false);
    }
  }, [open, duaId]);

  const handleSubcategoryChange = (subId) => {
    const sub = allSubcategories.find((s) => s.id === subId);
    setForm({
      ...form,
      subCategoryId: subId,
      category: sub ? sub.categoryId : form.category,
    });
  };

  const handleSave = async () => {
    if (!form.title.en || !form.textArabic) {
      setErrorMsg('Title (EN) and Arabic text are required.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      if (isEdit) {
        await updateDua(duaId, form);
      } else {
        await createDua(form);
      }
      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save Dua.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <FavoriteIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'দো‘আ সম্পাদনা (Edit Dua)' : 'নতুন দো‘আ যোগ করুন (Add New Dua)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${duaId} • ${form.title.en || 'Dua'}` : 'সকল ভাষা ও বিবরণ সহ দো‘আ সংরক্ষণ করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={350}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              দো‘আর তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>দো‘আর শিরোনাম ও মূল আরবি পাঠ</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MultiLangTextField
                    fields={form.title}
                    onChange={(title) => setForm({ ...form, title })}
                    label="Title"
                    title="Title"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    label="মূল আরবি পাঠ (Arabic Text) *"
                    value={form.textArabic}
                    onChange={(e) => setForm({ ...form, textArabic: e.target.value })}
                    className={`${classes.field} ${classes.arabicField}`}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultiLangTextField
                    fields={form.transliteration}
                    onChange={(transliteration) => setForm({ ...form, transliteration })}
                    label="উচ্চারণ (Transliteration)"
                    title="Transliteration"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>অনুবাদ (Translation - English &amp; Bangla)</Typography>

              {/* English Translation */}
              <Box className={classes.editorContainer}>
                <Box className={classes.editorLabel}>
                  <span style={{ flexGrow: 1 }}>English Translation (HTML / Rich Text)</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML((prev) => ({ ...prev, translationEn: !prev.translationEn }))}
                    title="Edit HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setShowPreview((prev) => ({ ...prev, translationEn: !prev.translationEn }))}
                    title="Preview HTML"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.translationEn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    variant="outlined"
                    value={form.translation.en || ''}
                    onChange={(e) => setForm({ ...form, translation: { ...form.translation, en: e.target.value } })}
                    className={classes.field}
                    placeholder="Edit HTML source..."
                  />
                ) : showPreview.translationEn ? (
                  <Box
                    className={classes.field}
                    style={{ border: '1px solid #cbd5e1', padding: 12, minHeight: 120, borderRadius: 10 }}
                    dangerouslySetInnerHTML={{ __html: form.translation.en || '' }}
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.translation.en || ''}
                    onChange={(val) => setForm({ ...form, translation: { ...form.translation, en: val } })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write English translation here..."
                  />
                )}
              </Box>

              {/* Bangla Translation */}
              <Box className={classes.editorContainer} style={{ marginTop: 20 }}>
                <Box className={classes.editorLabel}>
                  <span style={{ flexGrow: 1 }}>Bangla Translation (HTML / Rich Text)</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML((prev) => ({ ...prev, translationBn: !prev.translationBn }))}
                    title="Edit HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setShowPreview((prev) => ({ ...prev, translationBn: !prev.translationBn }))}
                    title="Preview HTML"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.translationBn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    variant="outlined"
                    value={form.translation.bn || ''}
                    onChange={(e) => setForm({ ...form, translation: { ...form.translation, bn: e.target.value } })}
                    className={classes.field}
                    placeholder="Edit HTML source..."
                  />
                ) : showPreview.translationBn ? (
                  <Box
                    className={classes.field}
                    style={{ border: '1px solid #cbd5e1', padding: 12, minHeight: 120, borderRadius: 10 }}
                    dangerouslySetInnerHTML={{ __html: form.translation.bn || '' }}
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.translation.bn || ''}
                    onChange={(val) => setForm({ ...form, translation: { ...form.translation, bn: val } })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="বাংলায় অনুবাদ লিখুন..."
                  />
                )}
              </Box>
            </Paper>

            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>ক্যাটাগরি ও মিডিয়া সেটিংস</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="subcategory-label">সাব-ক্যাটাগরি (Subcategory)</InputLabel>
                    <Select
                      labelId="subcategory-label"
                      value={form.subCategoryId || ''}
                      onChange={(e) => handleSubcategoryChange(e.target.value)}
                      label="সাব-ক্যাটাগরি (Subcategory)"
                    >
                      <MenuItem value="">
                        <em>Select a subcategory...</em>
                      </MenuItem>
                      {Object.entries(groupedSubcategories).map(([catId, subs]) => [
                        <MenuItem
                          key={`header-${catId}`}
                          value={`header-${catId}`}
                          disabled
                          style={{ fontWeight: 700, opacity: 0.7, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase' }}
                        >
                          {catId}
                        </MenuItem>,
                        ...subs.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id} style={{ paddingLeft: 24 }}>
                            {sub.titleBn || sub.titleEn}
                          </MenuItem>
                        )),
                      ])}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Audio URL (ঐচ্ছিক)"
                    placeholder="https://..."
                    value={form.audioUrl}
                    onChange={(e) => setForm({ ...form, audioUrl: e.target.value })}
                    className={classes.field}
                  />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <FormControlLabel
          control={
            <Switch
              checked={form.isPublished}
              onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {form.isPublished ? 'অবস্থা: প্রকাশিত (Published)' : 'অবস্থা: ড্রাফট (Draft)'}
            </Typography>
          }
        />

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            className={classes.saveBtn}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'দো‘আ প্রকাশ করুন (Publish Dua)'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
